import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
// Updated import: Using Truck instead of Motorbike
import { Clock, CheckCircle, AlertCircle, Truck } from 'lucide-react';
import { setupOrdersListener } from '../store/slices/authslice';

// Define order statuses
const OrderStatus = {
  PENDING: 'pending',
  PREPARING: 'preparing',
  READY: 'ready',
};

const Orders = () => {
  const { orders, user, error } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  // Sort orders with newest first
  const sortedOrders = [...orders].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  /**
   * Returns configuration for the order status badge.
   * Each status gets a specific icon, background, and label.
   */
  const getStatusConfig = (status) => {
    const configs = {
      [OrderStatus.PENDING]: {
        icon: Clock,
        className: 'bg-red-600 text-white',
        label: 'Pending',
      },
      [OrderStatus.PREPARING]: {
        icon: AlertCircle,
        className: 'bg-red-700 text-white',
        label: 'Preparing',
      },
      [OrderStatus.READY]: {
        icon: CheckCircle,
        className: 'bg-red-600 text-white',
        label: 'Ready',
      },
    };
    return configs[status] || configs[OrderStatus.PENDING];
  };

  // Listen to live orders updates when a user is logged in
  useEffect(() => {
    let unsubscribe;
    if (user?.uid) {
      dispatch(setupOrdersListener(user.uid)).then((action) => {
        unsubscribe = action.payload;
      });
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [user?.uid, dispatch]);

  // Display a log-in prompt if no user is available
  if (!user?.uid) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-black">
        <h2 className="text-2xl font-semibold mb-2 text-red-500">Please Log In</h2>
        <p className="text-gray-400">Sign in to view your orders</p>
      </div>
    );
  }

  // Display any errors that might have occurred
  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  /**
   * OrderItem component renders the details for a single order item.
   */
  const OrderItem = ({ item }) => (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="font-medium text-red-500">
          {item.name}{' '}
          {item.type && (
            <span className="text-sm text-gray-400">({item.type})</span>
          )}
        </span>
        <span className="text-gray-300">₦{item.totalPrice.toLocaleString()}</span>
      </div>
      <div className="flex items-center text-sm text-gray-400">
        <span>Qty: {item.quantity}</span>
      </div>
      {item.extras &&
        Object.entries(item.extras).map(([category, extras]) => (
          <div key={category} className="ml-4 text-sm text-gray-400">
            {extras.map((extra) => (
              <div key={extra.name} className="flex justify-between">
                <span>
                  {extra.name} (x{extra.quantity})
                </span>
                <span>₦{extra.price * extra.quantity}</span>
              </div>
            ))}
          </div>
        ))}
    </div>
  );

  /**
   * Optionally renders a chef comment if one is provided.
   */
  const renderChefComment = (comment) => {
    if (comment && comment.trim() !== '') {
      return (
        <div className="mt-2 flex items-center text-gray-400">
          <span className="mr-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </span>
          <span className="font-bold">P.S.: {comment}</span>
        </div>
      );
    }
    return null;
  };

  /**
   * OrderCard component renders an individual order.
   * It includes order header, amount, date, delivery details,
   * and, if applicable (in Preparing or Ready orders), the assigned rider info.
   */
  const OrderCard = ({ order }) => {
    const status = getStatusConfig(order.status);
    const StatusIcon = status.icon;

    return (
      <div className="bg-black rounded-xl overflow-hidden shadow-lg border border-gray-800 mb-4">
        <div className="p-6 space-y-4">
          {/* Order Header */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-400">
              Order #{order.orderId.slice(-6)}
            </div>
            <div
              className={`px-3 py-1 rounded-full flex items-center gap-1 text-sm ${status.className}`}
            >
              <StatusIcon className="w-3 h-3" />
              <span>{status.label}</span>
            </div>
          </div>

          {/* Order Amount & Date */}
          <div className="text-xl font-semibold text-red-500">
            ₦{order.totalAmount.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">
            {new Date(order.createdAt).toLocaleDateString()} at{' '}
            {new Date(order.createdAt).toLocaleTimeString()}
          </div>
          <div className="h-px bg-gray-800" />

          {/* Delivery Details */}
          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium text-red-500 mb-2">
                Delivery Details
              </div>
              <div className="text-sm text-gray-400">
                Method: {order.deliveryMethod}
                {order.deliveryMethod === 'delivery' && order.deliveryAddress && (
                  <div className="mt-1">Address: {order.deliveryAddress}</div>
                )}
              </div>
            </div>

            {/* Render Assigned Rider details if in Preparing or Ready status */}
            {(order.status === OrderStatus.PREPARING ||
              order.status === OrderStatus.READY) &&
              order.assignedRider && (
                <div className="mt-4">
                  <div className="text-sm font-medium text-red-500 mb-2">
                    Assigned Rider
                  </div>
                  <div className="flex items-center text-sm text-gray-400">
                    <Truck className="w-4 h-4" color="#374151" />
                    <div className="ml-2">
                      <div>{order.assignedRider.name}</div>
                      <div>{order.assignedRider.phone}</div>
                    </div>
                  </div>
                </div>
              )}

            <div className="h-px bg-gray-800" />

            {/* Order Items */}
            <div className="space-y-3">
              <div className="text-sm font-medium text-red-500">
                Order Items
              </div>
              {order.items.map((item, index) => (
                <OrderItem key={index} item={item} />
              ))}
            </div>
          </div>

          {/* Optional Chef Comment */}
          {renderChefComment(order.items[0]?.chefComment)}

          {/* Payment Details Section */}
          {order.paymentReference && (
            <div className="mt-4 p-4 bg-gray-900 rounded-lg border-t border-gray-800">
              <h4 className="text-lg font-medium text-red-500 mb-2">
                Payment Details
              </h4>
              <p className="text-sm text-gray-400">
                Payment Reference:{' '}
                <span className="font-medium">{order.paymentReference}</span>
              </p>
              {order.differentPayer && order.payerName && (
                <p className="text-sm text-gray-400">
                  Payer Name: <span className="font-medium">{order.payerName}</span>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * Helper function to filter orders by their status.
   */
  const filterOrdersByStatus = (status) =>
    sortedOrders.filter((order) => order.status === status);

  return (
    <div className="min-h-screen bg-black">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-semibold mb-6 text-red-500">Your Orders</h1>
        <Tabs>
          <TabList className="flex space-x-4 mb-4">
            {/* Pending Tab with counter badge */}
            <Tab className="px-4 py-2 bg-black text-red-500 hover:bg-gray-800 rounded-lg relative">
              Pending
              {filterOrdersByStatus(OrderStatus.PENDING).length > 0 && (
                <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                  {filterOrdersByStatus(OrderStatus.PENDING).length}
                </span>
              )}
            </Tab>

            {/* Preparing Tab with counter badge */}
            <Tab className="px-4 py-2 bg-black text-red-500 hover:bg-gray-800 rounded-lg relative">
              Preparing
              {filterOrdersByStatus(OrderStatus.PREPARING).length > 0 && (
                <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                  {filterOrdersByStatus(OrderStatus.PREPARING).length}
                </span>
              )}
            </Tab>

            {/* Ready Tab */}
            <Tab className="px-4 py-2 bg-black text-red-500 hover:bg-gray-800 rounded-lg">
              Ready
            </Tab>
          </TabList>

          {/* Pending Orders Panel */}
          <TabPanel>
            {filterOrdersByStatus(OrderStatus.PENDING).length > 0 ? (
              filterOrdersByStatus(OrderStatus.PENDING).map((order) => (
                <OrderCard key={order.id} order={order} />
              ))
            ) : (
              <div className="bg-black rounded-xl p-8 text-center mb-4">
                <p className="text-red-500">No pending orders.</p>
              </div>
            )}
          </TabPanel>

          {/* Preparing Orders Panel */}
          <TabPanel>
            {filterOrdersByStatus(OrderStatus.PREPARING).length > 0 ? (
              filterOrdersByStatus(OrderStatus.PREPARING).map((order) => (
                <OrderCard key={order.id} order={order} />
              ))
            ) : (
              <div className="bg-black rounded-xl p-8 text-center mb-4">
                <p className="text-red-500">No orders in preparation.</p>
              </div>
            )}
          </TabPanel>

          {/* Ready Orders Panel */}
          <TabPanel>
            {filterOrdersByStatus(OrderStatus.READY).length > 0 ? (
              filterOrdersByStatus(OrderStatus.READY).map((order) => (
                <OrderCard key={order.id} order={order} />
              ))
            ) : (
              <div className="bg-black rounded-xl p-8 text-center mb-4">
                <p className="text-red-500">No ready orders.</p>
              </div>
            )}
          </TabPanel>
        </Tabs>

        {/* Custom CSS overrides for the tabs */}
        <style jsx>{`
          .react-tabs__tab:focus,
          .react-tabs__tab--selected:focus {
            outline: none;
            box-shadow: none;
          }
          .react-tabs__tab--selected {
            background: #1a1a1a;
            color: #ff4136 !important;
            border: 1px solid #ff4136;
            border-bottom: none;
          }
        `}</style>
      </div>
    </div>
  );
};

export default Orders;
